import { useState, useRef, useEffect } from 'react';
import classnames from 'classnames';
import { getFlagURL } from '@scramble/utils';
import PhoneInput, {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumber,
  Country, isValidPhoneNumber,
} from 'react-phone-number-input';
import IconChevron from '@scramble/design-system/icons/chevron-down.svg?react';
import en from 'react-phone-number-input/locale/en.json';
import de from 'react-phone-number-input/locale/de.json';
import el from 'react-phone-number-input/locale/el.json';
import es from 'react-phone-number-input/locale/es.json';
import pt from 'react-phone-number-input/locale/pt.json';
import nl from 'react-phone-number-input/locale/nl.json';
import 'react-phone-number-input/style.css';
import { CountrySelectProps, PhoneNumberFieldProps, SupportedLocale } from './types';
import s from './index.module.scss';

const locales: Record<SupportedLocale, Record<string, string>> = { en, de, el, es, pt, nl };

function CountrySelect({ value, onChange, error, placeholder, labels }: CountrySelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const countries = getCountries();
  const filtered = countries.filter((c) => labels[c]?.toLowerCase().includes(search.toLowerCase()));

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className={s.country_select_wrapper} ref={ref}>
      <div
        className={classnames(
          s.country_display,
          open && s.country_display_focused,
          error && s.country_display_error
        )}
        onClick={() => setOpen((o) => !o)}
      >
        <img
          src={getFlagURL(value)}
          alt={value}
          width={20}
          height={20}
          className={s.country_flag}
        />
        <span className={s.country_dial_code}>+{getCountryCallingCode(value)}</span>
        <IconChevron className={s.chevron_icon} />
      </div>

      {open && (
        <div className={s.country_dropdown}>
          <div className={s.country_search}>
            <input
              autoFocus
              type="text"
              placeholder={placeholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <ul className={s.country_list}>
            {filtered.map((c) => (
              <li
                key={c}
                className={`${s.country_item} ${c === value ? s.active : ''}`}
                onClick={() => {
                  onChange(c);
                  setOpen(false);
                  setSearch('');
                }}
              >
                <img src={getFlagURL(c)} alt={c} />
                <span className={s.country_name}>{labels[c]}</span>
                <span className={s.country_code}>+{getCountryCallingCode(c)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function PhoneNumberField({
  label,
  name = 'phone',
  value,
  error,
  desc,
  placeholder,
  countryPlaceholder,
  onChange,
  onValidate,
  locale = 'en',
}: PhoneNumberFieldProps) {
  const labels = locales[locale] || en;

  const [country, setCountry] = useState<Country>(() => {
    if (value) {
      try {
        return parsePhoneNumber(value)?.country || 'GB';
      } catch {
        return 'GB';
      }
    }
    return 'GB';
  });

  useEffect(() => {
    onValidate && onValidate(isValidPhoneNumber(value || ''));
  }, [value]);

  return (
    <div className={s.phone_field_wrapper}>
      {label && (
        <label className={s.phone_label} htmlFor={name}>
          {label}
        </label>
      )}
      <PhoneInput
        country={country}
        onCountryChange={(c) => c && setCountry(c)}
        placeholder={placeholder}
        value={value}
        id={name}
        onChange={onChange}
        countrySelectComponent={(props) => (
          <CountrySelect
            {...props}
            error={error}
            labels={labels}
            placeholder={countryPlaceholder}
          />
        )}
        className={classnames(s.phone_input_root, error && s.phone_input_root_error)}
        addInternationalOption={false}
        international={false}
        limitMaxLength={true}
      />
      {error ? (
        <div className={s.phone_error}>{error}</div>
      ) : (
        desc && <div className={s.phone_desc}>{desc}</div>
      )}
    </div>
  );
}